import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack} from 'views/components';
import {translate} from 'views/module';
import {BarChartRounded, PauseCircleOutline, CancelOutlined} from 'views/icons';

import FilterBarChip from './filter_bar/FilterBarChip';
import FilterBarArrow from './filter_bar/FilterBarArrow';

import backend from 'utils/backend';

const FilterBar = (props) => {
  const {selection, filterOptions, updateFilter, reload} = props;

  const [chipCounts, setChipCounts] = React.useState({});

  const statusFilter = filterOptions?.status;
  if (!statusFilter) {
    return null;
  }

  const selectedKey = selection?.filter?.status?.optionSelected || 'ALL';
  const statusMap = translate('client.Status');
  const options = Object.values(statusFilter.options)
      .sort((a, b) => a.rank - b.rank);
  const primaryOptions = options.filter((option) => statusMap[option.key]?.primary);
  const secondaryOptions = options.filter((option) => {
    if (option.key === 'ALL') return true;
    return statusMap[option.key]?.secondary;
  });

  React.useEffect(() => {
    const loadCounts = async () => {
      try {
        const optionKeys = primaryOptions.map((option) => option.key);
        const trackedKeys = new Set(optionKeys);
        const counts = optionKeys.reduce((acc, key) => {
          acc[key] = 0;
          return acc;
        }, {});

        const sortBy = {
          sortKey: 'createdAt',
          sortDirection: 'DESC',
        };

        let lastEvaluatedKey = null;
        do {
          const res = await backend.client.queryClient([], sortBy, lastEvaluatedKey);
          const page = res.list;

          for (const item of page) {
            const value = item?.[statusFilter.key];
            if (trackedKeys.has(value)) {
              counts[value] += 1;
            }
          }

          lastEvaluatedKey = res?.lastEvaluatedKey;
        } while (lastEvaluatedKey);

        setChipCounts(counts);
      } catch (error) {
        setChipCounts({});
      }
    };

    loadCounts();
  }, [reload]);

  return (
    <Box
      sx={{
        width: 1,
      }}
    >
      <Stack
        spacing={{xs: 1.25, md: 1.5}}
        sx={{
          px: {xs: 1.5, md: 2.5},
          py: {xs: 0.5, md: 1},
          minWidth: 'max-content',
        }}
      >
        <Stack direction='row' alignItems='center'>
          {
            primaryOptions.map((option, index) => {
              const isSelected = option.key === selectedKey;

              const optionColors = statusMap[option.key];
              const accentColor = optionColors.Color;
              const selectedColor = optionColors.ColorSelected;
              const borderColor = optionColors.ColorBorder;

              return (
                <Box key={option.key} sx={{position: 'relative'}}>
                  <FilterBarChip
                    label={option.label}
                    count={chipCounts[option.key]}
                    isSelected={isSelected}
                    onClick={() => updateFilter(statusFilter, option)}
                    sx={{
                      px: {xs: 2, md: 3},
                      py: {xs: 1.6, md: 1.85},
                      borderTopLeftRadius: index === 0 ? 14 : 0,
                      borderBottomLeftRadius: index === 0 ? 14 : 0,
                      borderTopRightRadius: index === primaryOptions.length - 1 ? 14 : 0,
                      borderBottomRightRadius: index === primaryOptions.length - 1 ? 14 : 0,
                      borderColor: isSelected ? selectedColor : borderColor,
                      backgroundColor: isSelected ? selectedColor : accentColor,
                    }}
                  />

                  {index < primaryOptions.length - 1 && <FilterBarArrow />}
                </Box>
              );
            })
          }
        </Stack>
        <Stack direction='row' spacing={{xs: 1.5, md: 2}} alignItems='center'>
          {
            secondaryOptions.map((option) => {
              const isSelected = option.key === selectedKey;

              let icon = null;
              if (option.key === 'ALL') icon = BarChartRounded;
              if (option.key === 'STALLED') icon = PauseCircleOutline;
              if (option.key === 'CLOSED_LOST') icon = CancelOutlined;

              return (
                <FilterBarChip
                  key={option.key}
                  label={option.label}
                  icon={icon}
                  isSelected={isSelected}
                  onClick={() => updateFilter(statusFilter, option)}
                  sx={{
                    borderColor: isSelected ? '#000000' : '#FFFFFF',
                    backgroundColor: isSelected ? '#000000' : '#FFFFFF',
                    boxShadow: '0 2px 12px 0 rgba(60,60,60,0.15)',
                  }}
                />
              );
            })
          }
        </Stack>
      </Stack>
    </Box>
  );
};

FilterBar.propTypes = {
  selection: PropTypes.object.isRequired,
  filterOptions: PropTypes.object.isRequired,
  updateFilter: PropTypes.func.isRequired,
  reload: PropTypes.number.isRequired,
};

export default React.memo(FilterBar);
